import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchMyPermissions, fetchRoles, DB_NAME_TO_KEY } from '@/features/permissions/api'
import { currentUser } from '@/lib/api'

const PERM_CACHE_KEY = 'ps_permissions'
const ROLE_KEY_CACHE = 'ps_role_key'

function loadCachedPerms(): string[] {
  try {
    const raw = localStorage.getItem(PERM_CACHE_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function loadCachedRoleKey(): string | null {
  try { return localStorage.getItem(ROLE_KEY_CACHE) } catch { return null }
}

export function clearPermissionCache() {
  localStorage.removeItem(PERM_CACHE_KEY)
  localStorage.removeItem(ROLE_KEY_CACHE)
}

interface PermissionContextValue {
  permissions: Set<string>
  can: (key: string) => boolean
  isSuperAdmin: boolean
  roleKey: string | null | undefined
  isPermissionsReady: boolean
}

const PermissionContext = createContext<PermissionContextValue>({
  permissions: new Set(),
  can: () => false,
  isSuperAdmin: false,
  roleKey: null,
  isPermissionsReady: false,
})

export function PermissionProvider({ children }: { children: ReactNode }) {
  const enabled = !!currentUser.get()
  const stored = currentUser.get()
  const roleId = stored?.role_id
  // role_name is now returned by the login API, so this resolves synchronously
  // from localStorage without any network request.
  const roleKeyFromName = stored?.role_name ? (DB_NAME_TO_KEY[stored.role_name] ?? null) : null

  const { data: permData, isFetching: permsFetching } = useQuery({
    queryKey: ['my-permissions'],
    queryFn: async () => {
      const keys = await fetchMyPermissions()
      try { localStorage.setItem(PERM_CACHE_KEY, JSON.stringify(keys)) } catch {}
      return keys
    },
    enabled,
    initialData: loadCachedPerms,
    // 0 = epoch, so React Query always considers the cached data stale and
    // fires a background refetch immediately on mount.
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60 * 1000,
  })

  const { data: roleKey } = useQuery({
    queryKey: ['my-role-key', roleId],
    queryFn: async () => {
      const roles = await fetchRoles()
      const key = roles.find((r) => r.roleId === roleId)?.key ?? null
      if (key) try { localStorage.setItem(ROLE_KEY_CACHE, key) } catch {}
      return key
    },
    enabled: enabled && roleId !== undefined,
    // Prefer role_name from login response (synchronous, env-independent),
    // fall back to the cached value from a previous fetchRoles call.
    initialData: () => roleKeyFromName ?? loadCachedRoleKey(),
    // role_name from login is always current; old localStorage cache is stale.
    initialDataUpdatedAt: roleKeyFromName ? Date.now() : 0,
    staleTime: 30 * 60 * 1000,
  })

  const permissions = useMemo(() => new Set(permData ?? []), [permData])
  const can = useMemo(() => (key: string) => permissions.has(key), [permissions])
  const isSuperAdmin = roleKey === 'super_admin' || roleId === 3
  // Ready once we have cached permissions OR the first fetch has settled.
  const isPermissionsReady = permissions.size > 0 || !permsFetching

  return (
    <PermissionContext.Provider value={{ permissions, can, isSuperAdmin, roleKey, isPermissionsReady }}>
      {children}
    </PermissionContext.Provider>
  )
}

export function usePermissions() {
  return useContext(PermissionContext)
}

export function usePermission(key: string) {
  const { can } = useContext(PermissionContext)
  return { allowed: can(key) }
}
